import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { DataCache } from '../../DataCache';
import { Resources } from '@shared/localizations/Resources';
import { AccountType } from '@shared/utils/cache/Accounts/Account.types';
import { InstrumentDeliveryType } from '@shared/utils/Instruments/InstrumentDelivertyType';
import { InstrumentTradingBalance } from '@shared/utils/Instruments/InstrumentTradingBalance';
import { getProductTypeKey } from '@shared/utils/Instruments/ProductType';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { CurrencyUtils } from '@shared/utils/Asset/CurrencyUtils';
import { BoughtSoldUtils } from '@shared/utils/Instruments/BoughtSoldUtils';
import { BaseSettings } from '../../Settings/BaseGeneralSettingsWrapper';
import { StringUtils } from '@shared/utils/StringUtils';
class _FilledOrderFormatter {
    TradeId(trade) {
        return parseInt(trade.TradeId.split('-')[0]);
    }
    TradeIdStr(trade) {
        return trade.TradeId;
    }
    IdStr(trade) {
        return trade.OrderId;
    }
    SymbolStr(trade) {
        const instrument = trade.Instrument;
        const instrumentStr = trade.InstrumentStr;
        if (instrument !== null && instrument !== undefined) {
            return instrument.DisplayShortName();
        }
        else if (instrumentStr !== null && instrumentStr !== undefined) {
            return trade.InstrumentStr;
        }
        else {
            return '';
        }
    }
    AccountStr(trade) {
        var _a, _b;
        return (_b = (_a = trade.Account) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '';
    }
    IsLong(trade) {
        return trade.BuySell === OperationType.Buy;
    }
    OrderTypeStr(trade) {
        const orderTypeObj = DataCache.OrderParameterContainer.GetOrderType(trade.OrderType);
        return orderTypeObj != null ? Resources.getResource('property.' + orderTypeObj.localizationKey()) : '';
    }
    Quantity(trade) {
        if (BaseSettings.isQuantityInLots) {
            return trade.Amount;
        }
        else {
            const instrument = trade.Instrument;
            if (instrument !== null && instrument !== undefined) {
                return trade.Amount * instrument.LotSize;
            }
            else {
                return trade.Amount;
            }
        }
    }
    QuantityStr(trade) {
        const amount = this.Quantity(trade);
        let amountStr = amount.toString();
        if (trade.Instrument !== null) {
            amountStr = DataCache.formatVolume(trade.Instrument, Math.abs(amount), BaseSettings.isQuantityInLots, trade.ProductType, trade.Account);
        }
        return amountStr;
    }
    Price(trade) {
        return trade.Price;
    }
    PriceStr(trade) {
        const price = this.Price(trade);
        const instrument = trade.Instrument;
        if (instrument !== null && instrument !== undefined) {
            return instrument.formatPricePrecision(price, false, false);
        }
        else {
            return price.toString();
        }
    }
    GrossPnL(trade, useAccCurrency = true) {
        return trade.PnL * (useAccCurrency ? trade.OpenCrossPrise : 1);
    }
    GrossPnLStr(trade) {
        const profit = this.GrossPnL(trade);
        if (profit === Number.MAX_VALUE) {
            return StringUtils.NOT_AVAILABLE;
        }
        else {
            return this.FormatPriceAtCorrectAsset(trade, profit);
        }
    }
    NetPnL(trade, useAccCurrency = true) {
        const netPnL = (trade.PnL - trade.Comission + trade.Swap) * this.CrossPrice(trade, useAccCurrency);
        return this.truncateValue(trade.Account, netPnL);
    }
    NetPnLStr(trade) {
        const profit = this.NetPnL(trade);
        if (profit === Number.MAX_VALUE) {
            return StringUtils.NOT_AVAILABLE;
        }
        else {
            return this.FormatPriceAtCorrectAsset(trade, profit);
        }
    }
    ExecutionFee(trade, useAccCurrency = true) {
        if (trade.Comission === Number.MAX_VALUE) {
            return trade.Comission;
        }
        let comission = -trade.Comission;
        if (comission === 0) {
            comission = 0;
        } // prevent minus zero
        return comission * this.CrossPrice(trade, useAccCurrency);
    }
    ExecutionFeeStr(trade) {
        const executionFee = this.ExecutionFee(trade);
        if (executionFee === Number.MAX_VALUE) {
            return StringUtils.NOT_AVAILABLE;
        }
        else {
            return this.FormatPriceAtCorrectAsset(trade, executionFee);
        }
    }
    GetBought(trade) {
        return BoughtSoldUtils.Bought(trade);
    }
    GetSold(trade) {
        return BoughtSoldUtils.Sold(trade);
    }
    GetBoughtStr(trade) {
        return BoughtSoldUtils.BoughtStr(trade);
    }
    GetSoldStr(trade) {
        return BoughtSoldUtils.SoldStr(trade);
    }
    Date(trade) {
        return trade.Time;
    }
    DateStr(trade) {
        return DateTimeUtils.formatDate(this.Date(trade), 'DD.MM.YYYY  HH:mm:ss');
    }
    TradeVolume(trade) {
        return trade.getTradeVolume();
    }
    TradeVolumeStr(trade) {
        const tradeVolume = this.TradeVolume(trade);
        return this.FormatPriceAtCorrectAsset(trade, tradeVolume);
    }
    Exposure(trade, useAccCurrency = true) {
        const instrument = trade.Instrument;
        const cross = this.CrossPrice(trade, useAccCurrency);
        if (!isNullOrUndefined(instrument)) {
            return (trade.Price *
                trade.Amount *
                instrument.LotSize *
                instrument.GetTickCost() *
                instrument.GetFaceValueMultiplier() *
                cross);
        }
        else {
            return trade.Price * trade.Amount * cross;
        }
    }
    ExposureStr(trade) {
        var _a, _b;
        const exposure = this.Exposure(trade);
        const account = trade.Account;
        if (account !== null && account !== undefined) {
            let formattedPrice = '';
            let currency = '';
            const instrument = trade.Instrument;
            if (trade.Account.AccountType === AccountType.MultiAsset &&
                instrument !== null &&
                instrument !== undefined) {
                const exp2Asset = trade.Instrument.DataCache.GetAssetByName(instrument.Exp2);
                if (exp2Asset !== null && exp2Asset !== undefined) {
                    formattedPrice = exp2Asset.formatPrice(exposure, true);
                    currency = exp2Asset.Name;
                }
                else {
                    formattedPrice = exposure.toString();
                    currency = trade.Instrument.Exp2;
                }
            }
            else {
                currency = (_b = (_a = trade.Account.assetBalanceDefault) === null || _a === void 0 ? void 0 : _a.Asset) === null || _b === void 0 ? void 0 : _b.Name;
                formattedPrice = trade.Account.formatPrice(exposure, true);
            }
            return CurrencyUtils.FormatWithCurrencySymbol(formattedPrice, currency);
        }
        else {
            return '';
        }
    }
    ProductType(trade) {
        return trade.ProductType;
    }
    ProductTypeStr(trade) {
        const instrument = trade.Instrument;
        if (instrument !== null && instrument !== undefined) {
            return InstrumentUtils.GetLocalizedProductType(instrument, trade.ProductType);
        }
        else {
            return Resources.getResource('ProductType.' + getProductTypeKey(trade.ProductType));
        }
    }
    Rebates(trade, useAccCurrency = false) {
        return trade.Rebates * this.CrossPrice(trade, useAccCurrency);
    }
    RebatesStr(trade) {
        const rebates = this.Rebates(trade);
        return this.FormatPriceAtCorrectAsset(trade, rebates);
    }
    TradingExchangeStr(trade) {
        return trade.TradingExchange;
    }
    truncateValue(account, value) {
        return account ? account.roundPrice(value) : value;
    }
    CrossPrice(trade, useAccCurrency = false) {
        if (!useAccCurrency) {
            const account = trade.Account;
            const instrument = trade.Instrument;
            const buySell = trade.BuySell;
            if (!isNullOrUndefined(instrument)) {
                return trade.Instrument.DataCache.CrossRateCache.GetCrossPriceIns(account, instrument, buySell);
            }
            else {
                return 1;
            }
        }
        else {
            return trade.OpenCrossPrise;
        }
    }
    FormatPriceAtCorrectAsset(trade, price) {
        var _a, _b, _c;
        const acc = trade.Account;
        const instrument = trade.Instrument;
        if (acc !== null && acc !== undefined) {
            let formattedPrice;
            let currency;
            if (acc.AccountType === AccountType.MultiAsset && instrument !== null && instrument !== undefined) {
                currency = this.GetTargetCurrency(trade);
                const asset = (_a = acc.GetAssetBalanceByName(currency)) === null || _a === void 0 ? void 0 : _a.Asset;
                formattedPrice =
                    asset !== null && asset !== undefined ? asset.formatPrice(price, true) : price.toString();
            }
            else {
                currency = (_c = (_b = acc.assetBalanceDefault) === null || _b === void 0 ? void 0 : _b.Asset) === null || _c === void 0 ? void 0 : _c.Name;
                formattedPrice = acc.formatPrice(price, true);
            }
            return CurrencyUtils.FormatWithCurrencySymbol(formattedPrice, currency);
        }
        else {
            return price.toString();
        }
    }
    GetTargetCurrency(trade) {
        const account = trade.Account;
        const instrument = trade.Instrument;
        const isBuy = this.IsLong(trade);
        if (account.AccountType === AccountType.MultiAsset) {
            if (instrument.DeliveryMethod === InstrumentDeliveryType.Delivery_Physical &&
                instrument.TradingBalance === InstrumentTradingBalance.SETTLEMENT_IMMEDIATE) {
                return isBuy ? instrument.Exp1 : instrument.Exp2;
            }
            return instrument.Exp2;
        }
        return instrument.Exp2;
    }
}
export const FilledOrderFormatter = new _FilledOrderFormatter();
